/**
 * Commands to the barcode gun — its beeper, its LEDs and its settings.
 *
 * On the forklift tablets this site runs inside DockAutomation Tablet Browser,
 * the Android app that also holds the gun. A command is one direct message to
 * that app (see `deviceBridge.ts`), which translates the names into Zebra's
 * codes. Everywhere else — a desktop, a phone — every call is a no-op that
 * SAYS so, rather than pretending it fired.
 *
 * This file only SENDS. What the gun should sound like is not decided here:
 * which tone means what, and how the gun is set, is `gunFeedback.ts`. Change
 * what the dock sounds like there, not here.
 */

import { postToDevice } from './deviceBridge'
import { useScreenFlash } from '../../composables/useScreenFlash'
import { gunCues } from './gunSounds'
import {
  cueCommands, gunDefaults, isGunVolume, settingsCommands,
  type BridgeCommand, type GunColour, type GunSettings, type GunTone, type GunVolume, type ScannerSignal,
} from './gunFeedback'

export type { ScannerSignal }

/** `no-device` is a page that is not inside the tablet app, so there is no gun to tell. */
export type FeedbackResult = 'sent' | 'no-device'

function send(command: BridgeCommand): FeedbackResult {
  return postToDevice(command) ? 'sent' : 'no-device'
}

/** One pattern from the gun's vocabulary. The app drops a name it does not know. */
export function scannerBeep(tone: GunTone): FeedbackResult {
  return send({ command: 'beep', tone })
}

/** Light the gun's LED, then dark again after `ms`. */
export function scannerLed(colour: GunColour, ms = 800): FeedbackResult {
  return send({ command: 'led', colour, ms: String(ms) })
}

/**
 * How loud the gun's beeper is — a setting, not an event.
 *
 * The app holds it across reconnects and reboots. Never written to the gun
 * permanently: a level chosen for local testing must not follow the hardware
 * onto a dock.
 */
export function scannerVolume(level: GunVolume): FeedbackResult {
  return send({ command: 'volume', level })
}

/** Ask the app whether a gun is held; the answer arrives as a gun-status event. */
export function requestGunStatus(): FeedbackResult {
  return send({ command: 'status' })
}

/** Whether the gun beeps by itself on every decode. A setting, held like volume. */
export function scannerDecodeBeep(enabled: boolean): FeedbackResult {
  return send({ command: 'decode-beep', value: enabled ? 'on' : 'off' })
}

/**
 * The whole cue for one outcome, as gunFeedback.ts designed it.
 *
 * Fire-and-forget by design. Feedback is a courtesy on top of a decision the
 * screen has already made and recorded, so a missing gun must never change
 * what the app does — every failure is swallowed at the call site.
 */
export function signalScanner(signal: ScannerSignal): void {
  // Gun Sounds off in Settings: the lamps still say it, the beeper does not.
  for (const command of cueCommands(signal, gunCues())) send(command)
  // The screen goes red with the gun (ErrorFlash.vue): the one cue that does
  // not need the tablet app, so a tablet with no gun still shows the refusal.
  if (signal === 'error') useScreenFlash().flashError()
}

// ── Settings ────────────────────────────────────────────────────────────────
//
// Defaults come from the build (NUXT_PUBLIC_GUN_VOLUME, resolved in
// gunFeedback.ts). On top of that sits a per-tablet override the gun page
// writes, so quietening one tablet survives a reload instead of lasting until
// the next page load turned it back up.
//
// The override is localStorage, which is per ORIGIN — so a level set against a
// dev server can never follow the app to the deployed one. That is a happy
// accident of the storage, but it is the behaviour we want, so do not "fix" it
// by moving the override somewhere shared.

const VOLUME_OVERRIDE_KEY = 'dockautomation.gunVolume'

/**
 * What this build was configured with, or '' outside a Nuxt context.
 *
 * Guarded because these helpers are plain functions, not composables: they run
 * from a plugin and from component setup, both of which have the Nuxt instance,
 * but '' is the honest answer anywhere else and resolves to the dock default.
 */
function configuredVolume(): string {
  try {
    return String(useRuntimeConfig().public.gunVolume ?? '')
  } catch {
    return ''
  }
}

/** The volume this build starts at, before any per-tablet override. */
export function buildGunVolume(): GunVolume {
  return gunDefaults(configuredVolume()).volume
}

/** The bench's per-tablet volume, kept in this browser. */
export function rememberGunVolume(level: GunVolume): void {
  try {
    localStorage.setItem(VOLUME_OVERRIDE_KEY, level)
  } catch {
    // No storage: the level still applies until the next startup send.
  }
}

/** What this tablet's gun should be set to right now: the build's default, with any local override. */
export function gunSettings(): GunSettings {
  const defaults = gunDefaults(configuredVolume())
  let override: unknown = null
  try {
    override = localStorage.getItem(VOLUME_OVERRIDE_KEY)
  } catch {
    override = null
  }
  return { ...defaults, volume: isGunVolume(override) ? override : defaults.volume }
}

/**
 * Put the gun into its designed state. Idempotent, so it is sent at startup
 * and again whenever the app comes back to the foreground — the tablet app
 * persists what it receives, so a send that misses (the page up before the
 * gun's side of the app at boot) is repaired by the next one.
 */
export function applyGunSettings(): FeedbackResult {
  let outcome: FeedbackResult = 'sent'
  for (const command of settingsCommands(gunSettings())) {
    const result = send(command)
    if (result !== 'sent') outcome = result
  }
  return outcome
}
